import React, { useContext, useEffect, useState } from 'react';

import { Button, Checkbox, Form, Segment } from 'semantic-ui-react';

import _isEmpty from 'lodash/isEmpty';
import _isEqual from 'lodash/isEqual';
import { arrayMove } from 'react-sortable-hoc';

import ConfirmModal from 'components/common/ConfirmModal';

import {
  DELETE_ACCORDION_PANEL_CONFIRM_CONTENT,
  DELETE_ACCORDION_PANEL_CONFIRM_HEADER,
  SORTABLE_ACCORDION_AXIS,
  SORTABLE_ACCORDION_HELPER_CLASSNAME,
} from './EditAccordionBlock.constants';
import {
  createDefaultPanel,
  getDefaultPanels,
  getPanelById,
  getPanelWithUpdatedContentNames,
  isValidPanels,
} from './EditAccordionBlock.utils';
import SortableAccordionList from './components/SortableAccordionList';
import { asyncSequentialMap } from '../../../../../../../libs/async';
import ErrorHandler from '../../../../../../../libs/errors';
import { useLatestRefValue } from '../../../../../utils/hooks';
import { ACCORDION_BLOCK_ELEMENT_TYPE } from '../../../../constants/types';
import { EditPageElementModalContext } from '../../EditPageElementModal.context';
import { deleteContentForPanel } from '../../EditPageElementModal.utils';
import {
  createContentConfiguration,
  updateContentConfiguration,
  updateContentElementFromSave,
} from '../EditTextBlock/EditTextBlock.utils';

const EditAccordionBlock = ({ element = {} }) => {
  const {
    page,
    registerCallback,
    onUpdateElementState,
    addValidationError,
    clearValidationErrors,
  } = useContext(EditPageElementModalContext);

  const latestElementRef = useLatestRefValue(element);
  const {
    panels: elementPanels = [],
    exclusive: elementExclusive,
    styled: elementStyled,
  } = element;
  const [panels, setPanels] = useState(
    _isEmpty(elementPanels) ? getDefaultPanels() : elementPanels,
  );
  const [isExclusive, setIsExclusive] = useState(elementExclusive);
  const [isStyled, setIsStyled] = useState(elementStyled);
  const [panelIndexToRemove, setPanelIndexToRemove] = useState(null);

  function updateAccordionElementState(property, value) {
    onUpdateElementState(ACCORDION_BLOCK_ELEMENT_TYPE, {
      ...latestElementRef.current,
      [property]: value,
    });
  }

  function updateAccordionState(onUpdate) {
    setPanels((prevPanels) => {
      const newPanels = onUpdate(prevPanels);
      updateAccordionElementState('panels', newPanels);
      return newPanels;
    });
  }

  function updatePanelNodeProperty(index, property, value) {
    updateAccordionState((prevPanels) =>
      prevPanels.map((panel, i) =>
        i === index
          ? {
              ...panel,
              [property]: {
                ...panel[property],
                content: value,
              },
            }
          : panel,
      ),
    );
  }

  function onTitleChange(index, value) {
    updatePanelNodeProperty(index, 'title', value);
  }

  function onContentChange(index, value) {
    updatePanelNodeProperty(index, 'content', value);
  }

  function onItemCreate(panelNum) {
    updateAccordionState((prevPanels) => [...prevPanels, createDefaultPanel(panelNum)]);
  }

  function onRemovePane(index) {
    updateAccordionState((prevPanels) => prevPanels.filter((_, i) => i !== index));
  }

  function onRemovePaneRequest(index) {
    setPanelIndexToRemove(index);
  }

  function onRemovePaneCancel() {
    setPanelIndexToRemove(null);
  }

  function onRemovePaneConfirm() {
    if (panelIndexToRemove === null) {
      return;
    }

    onRemovePane(panelIndexToRemove);
    setPanelIndexToRemove(null);
  }

  function onExclusiveChanged(_, { checked }) {
    setIsExclusive(() => {
      updateAccordionElementState('exclusive', checked);
      return checked;
    });
  }

  function onStyledChanged(_, { checked }) {
    setIsStyled(() => {
      updateAccordionElementState('styled', checked);
      return checked;
    });
  }

  function onSortEnd({ oldIndex, newIndex }) {
    updateAccordionState((prevPanels) => arrayMove(prevPanels, oldIndex, newIndex));
  }

  function onActiveChange(index, checked) {
    updateAccordionState((prevPanels) =>
      prevPanels.map((panel, i) => (i === index ? { ...panel, isActive: checked } : panel)),
    );
  }

  async function onSavePanelPart(panelPart, pageId) {
    try {
      const { contentNodeConfigurationId, componentConfigurationId, content, contentName } =
        panelPart;

      if (contentNodeConfigurationId && componentConfigurationId) {
        const { data: results } = await updateContentConfiguration(
          componentConfigurationId,
          contentNodeConfigurationId,
          {
            content,
            contentName,
          },
        );

        return results[0];
      }

      return createContentConfiguration(contentName, content, pageId);
    } catch (error) {
      ErrorHandler.capture(error);
      return panelPart;
    }
  }

  async function onSavePanel(currentPanel, originalPanel) {
    const { id: pageId } = page;
    const { title, content } = currentPanel;
    const { title: originalTitle, content: originalContent } = originalPanel;

    let finalTitle = title;
    if (!_isEqual(title, originalTitle)) {
      const result = await onSavePanelPart(title, pageId);
      finalTitle = updateContentElementFromSave(title, result);
    }

    let finalContent = content;
    if (!_isEqual(content, originalContent)) {
      const result = await onSavePanelPart(content, pageId);
      finalContent = updateContentElementFromSave(content, result);
    }

    return {
      ...currentPanel,
      title: finalTitle,
      content: finalContent,
    };
  }

  async function onSaveContent() {
    clearValidationErrors();

    const currentElement = latestElementRef.current;
    const previousPanels = panels || [];
    const { panels: currentPanels } = currentElement;

    // Handle updated panels
    const updatePanelResults = await asyncSequentialMap(
      currentPanels,
      async (currentPanel, index) => {
        const { id: currentPanelId } = currentPanel;
        const previousPanel = getPanelById(previousPanels, currentPanelId);
        const updatedPanel = getPanelWithUpdatedContentNames(currentPanel, index);

        return onSavePanel(updatedPanel, previousPanel);
      },
    );

    const updatedPanels = updatePanelResults
      .map(({ result, error }, index) => {
        if (error) {
          const { message } = result;
          addValidationError(`Error updating panel: ${message}`);
          return currentPanels[index];
        }
        return result;
      })
      .filter(Boolean);

    // Handle deleted panels
    // Find the ids of the panels that changed
    const currentPanelIds = currentPanels.map((panel) => panel.id);
    const previousPanelIds = previousPanels.map((panel) => panel.id);
    const deletedPanelIds = previousPanelIds.filter((id) => !currentPanelIds.includes(id));
    await Promise.all(
      deletedPanelIds.map(async (id) => {
        const deletedPanel = previousPanels.find((panel) => panel.id === id);
        await deleteContentForPanel(deletedPanel);
      }),
    );

    // Create final accordion block
    const finalElement = {
      ...currentElement,
      panels: updatedPanels,
      type: ACCORDION_BLOCK_ELEMENT_TYPE,
    };
    await onUpdateElementState(ACCORDION_BLOCK_ELEMENT_TYPE, finalElement);
    return finalElement;
  }

  useEffect(() => {
    clearValidationErrors();
    if (_isEmpty(panels)) {
      addValidationError('You must have at least one panel');
    } else if (!isValidPanels(panels)) {
      addValidationError('All panels must have content and title');
    }
  }, [panels]);

  useEffect(() => {
    registerCallback(ACCORDION_BLOCK_ELEMENT_TYPE, onSaveContent);
  }, []);

  return (
    <div className="sortable-accordion-container">
      <Segment className="accordion-settings-container">
        <Form>
          <Form.Group widths="equal">
            <Form.Field>
              <Checkbox
                toggle
                label="Use Styled Accordion"
                checked={isStyled}
                onChange={onStyledChanged}
              />
            </Form.Field>
            <Form.Field>
              <Checkbox
                toggle
                label="Open one panel at a time"
                checked={isExclusive}
                onChange={onExclusiveChanged}
              />
            </Form.Field>
          </Form.Group>
        </Form>
      </Segment>
      <SortableAccordionList
        panels={panels}
        onItemCreate={onItemCreate}
        onTitleChange={onTitleChange}
        onContentChange={onContentChange}
        onRemove={onRemovePaneRequest}
        onActiveChange={onActiveChange}
        onSortEnd={onSortEnd}
        useDragHandle
        axis={SORTABLE_ACCORDION_AXIS}
        lockAxis={SORTABLE_ACCORDION_AXIS}
        helperClass={SORTABLE_ACCORDION_HELPER_CLASSNAME}
      />
      <ConfirmModal
        open={panelIndexToRemove !== null}
        onClose={onRemovePaneCancel}
        header={DELETE_ACCORDION_PANEL_CONFIRM_HEADER}
        content={DELETE_ACCORDION_PANEL_CONFIRM_CONTENT}
        onCancel={onRemovePaneCancel}
        onConfirm={onRemovePaneConfirm}
        className="delete-accordion-panel-modal"
        confirmButton={<Button className="delete-red" content="YES" />}
        cancelButton={<Button className="action-button-gray" content="CANCEL" />}
      />
    </div>
  );
};

export default EditAccordionBlock;
